import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Button, 
  Table, 
  Badge, 
  Spinner,
  InputGroup,
  Form,
  Row,
  Col,
  Modal
} from 'react-bootstrap';
import { 
  FaVideo, 
  FaFileAlt, 
  FaLink,
  FaSearch,
  FaPlus,
  FaEdit,
  FaTrash
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import api from '../../../api/apiClient';
import { getAssetUrl } from '../../../utils/apiBase';
import { useAuth } from '../../../contexts/AuthContext';
import VltFormFields from '../../../common/components/vlt/VltFormFields';
import {
  getInitialVltFormData,
  mapVltToFormData,
  validateVltForm,
  appendVltFormData
} from '../../../common/utils/vltForm';

const OrgVltList = () => {
  const { user } = useAuth();
  const [vlts, setVlts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editingVlt, setEditingVlt] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);

  const [formData, setFormData] = useState(getInitialVltFormData());

  const [formErrors, setFormErrors] = useState({});
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [vltToDelete, setVltToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [instructors, setInstructors] = useState([]);

  useEffect(() => {
    fetchVlts();
  }, [filterStatus, filterType]);

  useEffect(() => {
    if (!showModal || !user?.organization_id) return;
    fetchInstructors(user.organization_id);
  }, [showModal, user?.organization_id]);

  const fetchInstructors = async (organizationId) => {
    try {
      const response = await api.vlt.getInstructors(organizationId);
      if (response.data.success) setInstructors(response.data.data || []);
    } catch (error) {
      toast.error('Failed to fetch instructors');
      setInstructors([]);
    }
  };

  const fetchVlts = async () => {
    try {
      setLoading(true);
      const params = {
        search: searchTerm,
        status: filterStatus !== 'all' ? filterStatus : '',
        type: filterType !== 'all' ? filterType : '',
        limit: 500
      };

      // Backend returns org VLTs + true global VLTs for org admins.
      // Keep both on the client (same pattern as OrgSurveyList) so globals are never stripped.
      const response = await api.vlt.getAll(params);
      if (response.data.success) {
        const vltData = Array.isArray(response.data.data) ? response.data.data : [];
        const orgId = user?.organization_id != null ? Number(user.organization_id) : null;
        const visibleVlts = vltData.filter((vlt) => {
          const vltOrgId = vlt?.organization_id != null && vlt.organization_id !== ''
            ? Number(vlt.organization_id)
            : null;
          const groupCount = Array.isArray(vlt?.groups)
            ? vlt.groups.length
            : (Array.isArray(vlt?.group_ids) ? vlt.group_ids.length : 0);

          // True global: no organization and no groups
          if (vltOrgId === null && groupCount === 0) return true;
          // Own organization VLTs
          return orgId != null && vltOrgId === orgId;
        });
        setVlts(visibleVlts);
      }
    } catch (error) {
      console.error('Error fetching VLTs:', error);
      toast.error('Failed to fetch VLTs');
    } finally {
      setLoading(false);
    }
  };


  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleCreateVlt = async () => {
    setEditingVlt(null);
    setFormData(getInitialVltFormData());
    setFormErrors({});
    setShowModal(true);
  };

  const handleEditVlt = async (vlt) => {
    const isOwnOrgVlt = vlt?.organization_id != null
      && Number(vlt.organization_id) === Number(user?.organization_id);
    if (!isOwnOrgVlt) {
      toast.info('Global VLTs are view-only and cannot be edited.');
      return;
    }
    setEditingVlt(vlt);
    setFormData(mapVltToFormData(vlt));
    setFormErrors({});
    setShowModal(true);
  };

  const handleTypeChange = (type) => {
    setFormData((prev) => ({
      ...prev,
      type,
      url_or_path: '',
      document_source: 'link',
      document_file: null,
      existing_document_path: '',
      video_source: 'link',
      video_file: null,
      existing_video_path: '',
      meeting_datetime: '',
      instructor_id: ''
    }));
    setFormErrors({});
  };

  const handleDocumentSourceChange = (document_source) => {
    setFormData((prev) => ({
      ...prev,
      document_source,
      url_or_path: '',
      document_file: null
    }));
    setFormErrors({});
  };

  const handleVideoSourceChange = (video_source) => {
    setFormData((prev) => ({
      ...prev,
      video_source,
      url_or_path: '',
      video_file: null
    }));
    setFormErrors({});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormErrors({});

    const errors = validateVltForm(formData, { editingVlt });
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    try {
      setModalLoading(true);
      const formDataToSend = appendVltFormData(formData, { includeOrganization: false });
      formDataToSend.append('organization_id', user.organization_id);

      const response = editingVlt
        ? await api.vlt.update(editingVlt.id, formDataToSend)
        : await api.vlt.create(formDataToSend);

      if (response.data.success) {
        toast.success(editingVlt ? 'VLT updated successfully' : 'VLT created successfully');
        setShowModal(false);
        setFormData(getInitialVltFormData());
        setFormErrors({});
        setEditingVlt(null);
        fetchVlts();
      } else {
        toast.error(response.data.message || 'Failed to save VLT');
      }
    } catch (error) {
      console.error('Error saving VLT:', error);
      toast.error(error.response?.data?.message || 'Failed to save VLT');
    } finally {
      setModalLoading(false);
    }
  };

  const handleDeleteVlt = (vlt) => {
    const isOwnOrgVlt = vlt?.organization_id != null
      && Number(vlt.organization_id) === Number(user?.organization_id);
    if (!isOwnOrgVlt) {
      toast.info('Global VLTs are view-only and cannot be deleted.');
      return;
    }
    setVltToDelete(vlt);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!vltToDelete) return;

    try {
      setDeleteLoading(true);
      const response = await api.vlt.delete(vltToDelete.id);
      if (response.data.success) {
        toast.success('VLT deleted successfully');
        setShowDeleteModal(false);
        setVltToDelete(null);
        fetchVlts();
      } else {
        toast.error(response.data.message || 'Failed to delete VLT');
      }
    } catch (error) {
      console.error('Error deleting VLT:', error);
      toast.error('Failed to delete VLT');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setVltToDelete(null);
  };

  const filteredVlts = vlts.filter(vlt => {
    const matchesSearch = vlt.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         vlt.description?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const getTypeIcon = (type) => {
    switch (type) {
      case 'video':
        return <FaVideo className="text-primary" />;
      case 'document':
        return <FaFileAlt className="text-success" />;
      case 'link':
        return <FaLink className="text-info" />;
      default:
        return null;
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'video':
        return 'primary';
      case 'document':
        return 'success';
      case 'link':
        return 'info';
      default:
        return 'secondary';
    }
  };

  if (loading) {
    return (
      <div className="p-3 sm:p-4 md:p-6 bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 min-h-screen">
        <div className="flex justify-center items-center" style={{ height: '400px' }}>
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-600 border-t-transparent"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 sm:p-4 md:p-6 bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 min-h-screen">
      {/* Header */}
      <div className="mb-4">
        <h5 className="fw-bold mb-0 text-xl sm:text-2xl md:text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600">
          Virtual-Led Training
        </h5>
      </div>

      {/* Filters */}
      <Row className="mb-4 g-2 sm:g-3">
        <Col xs={12} md={4} className="mb-2 mb-md-0">
          <div className="flex shadow-sm rounded-lg sm:rounded-xl overflow-hidden bg-white">
            <div className="flex items-center px-3 bg-white border-r border-gray-200">
              <FaSearch className="w-4 h-4 text-purple-600" />
            </div>
            <input
              type="text"
              placeholder="Search VLTs..."
              value={searchTerm}
              onChange={handleSearch}
              className="w-full px-3 py-2 border-0 focus:ring-2 focus:ring-purple-500 text-sm sm:text-base outline-none"
            />
          </div>
        </Col>
        <Col xs={12} sm={6} md={2} className="mb-2 mb-md-0">
          <Form.Select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="rounded-lg sm:rounded-xl shadow-sm border-purple-200 focus:ring-2 focus:ring-purple-500 text-xs sm:text-sm"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </Form.Select>
        </Col>
        <Col xs={12} sm={6} md={3} className="mb-2 mb-md-0">
          <Form.Select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="rounded-lg sm:rounded-xl shadow-sm border-purple-200 focus:ring-2 focus:ring-purple-500 text-xs sm:text-sm"
          >
            <option value="all">All Types</option>
            <option value="video">Video</option>
            <option value="document">Document</option>
            <option value="link">Meeting Link</option>
          </Form.Select>
        </Col>
        <Col xs={12} sm={6} md={3}>
          <Button
            bsPrefix="custom-hamburger"
            onClick={handleCreateVlt}
            className="w-full border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2"
          >
            <FaPlus className="w-4 h-4" />
            <span className="whitespace-nowrap">Create VLT</span>
          </Button>
        </Col>
      </Row>

      {/* VLTs Table */}
      <Card className="border-0 shadow-lg rounded-xl overflow-hidden">
        <Card.Body className="p-0">
          {filteredVlts.length === 0 ? (
            <div className="text-center py-5 bg-white">
              <div className="flex justify-center items-center mb-3">
                <FaFileAlt className="text-muted" style={{ fontSize: '2rem' }} />
              </div>
              <p className="text-muted mb-0">
                {searchTerm ? 'No VLTs found matching your search.' : 'No VLTs created yet.'}
              </p>
            </div>
       
       
          ) : (
            <div className="table-responsive">
              <Table hover className="mb-0">
                <thead className="bg-gradient-to-r from-purple-600 to-pink-600 text-white">
                  <tr>
                    <th className="py-2 px-3">VLT</th>
                    <th className="py-2 px-3">Type</th>
                    <th className="py-2 px-3">Audience</th>
                    <th className="py-2 px-3">Status</th>
                    <th className="py-2 px-3">Created</th>
                    <th className="py-2 px-3 text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredVlts.map(vlt => {
                    const isOwnOrgVlt = vlt.organization_id != null
                      && Number(vlt.organization_id) === Number(user?.organization_id);
                    const isGlobalVlt = (vlt.organization_id == null || vlt.organization_id === '')
                      && !(vlt.groups?.length);
                    return (
                    <tr key={vlt.id} className="border-b border-purple-50 hover:bg-purple-50/60 transition-all">
                      <td className="py-2 px-3 align-middle">
                        <div className="d-flex align-items-center gap-2">
                          <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white shadow-sm">
                            {vlt.thumbnail_image ? (
                              <img
                                src={getAssetUrl(vlt.thumbnail_image)}
                                alt="VLT Thumbnail"
                                className="w-full h-full rounded-lg object-cover"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                }}
                              />
                            ) : (
                              getTypeIcon(vlt.type)
                            )}
                          </div>
                          <div>
                            <div className="fw-semibold text-gray-800 text-sm">{vlt.title}</div>
                            <small className="text-muted text-xs">
                              {vlt.description?.substring(0, 60)}
                              {vlt.description?.length > 60 && '...'}
                            </small>
                          </div>
                        </div>
                      </td>
                      <td className="py-2 px-3 align-middle">
                        <Badge bg={getTypeColor(vlt.type)} className="d-flex align-items-center w-fit">
                          {getTypeIcon(vlt.type)}
                          <span className="ms-1 text-capitalize">{vlt.type}</span>
                        </Badge>
                      </td>
                      <td className="py-2 px-3 align-middle text-gray-600 text-sm">
                        {isGlobalVlt
                          ? <span className="text-purple-700 font-medium">Global</span>
                          : vlt.groups?.length
                            ? vlt.groups.map((g) => g.name).join(', ')
                            : <span>Organization learners</span>}
                      </td>
                      <td className="py-2 px-3 align-middle">
                        <Badge bg={vlt.status === 'active' ? 'success' : 'secondary'} className="text-capitalize">
                          {vlt.status}
                        </Badge>
                      </td>
                      <td className="py-2 px-3 align-middle text-gray-600 text-xs">
                        {new Date(vlt.createdAt).toLocaleDateString()}
                      </td>
                      <td className="py-2 px-3 align-middle">
                        <div className="d-flex gap-1 justify-content-end align-items-center">
                          {isOwnOrgVlt ? (
                            <>
                              <Button
                                bsPrefix="custom-hamburger"
                                onClick={() => handleEditVlt(vlt)}
                                className="border-0 border-purple-300 text-purple-600 rounded hover:bg-purple-50 transition p-1"
                                title="Edit VLT"
                              >
                                <FaEdit className="w-5 h-5" />
                              </Button>
                              <Button
                                bsPrefix="custom-hamburger"
                                onClick={() => handleDeleteVlt(vlt)}
                                className="border-0 border-red-300 text-red-600 hover:bg-red-50 rounded transition p-1"
                                title="Delete VLT"
                              >
                                <FaTrash className="w-5 h-5" />
                              </Button>
                            </>
                          ) : (
                            <span className="text-xs text-gray-500 px-2 whitespace-nowrap">
                              {isGlobalVlt ? 'Global · View only' : 'View only'}
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Create/Edit Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered>
        <Modal.Header closeButton closeVariant="white" className="bg-gradient-to-r from-purple-600 to-pink-600 text-white border-0 py-3 px-4 sm:px-4 sm:py-4 modal-header">
          <Modal.Title className="font-bold text-xl text-white">
            {editingVlt ? 'Edit VLT' : 'Create VLT'}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body className="bg-gray-50 p-3 sm:p-5">
            <VltFormFields
              formData={formData}
              formErrors={formErrors}
              onChange={setFormData}
              onTypeChange={handleTypeChange}
              onDocumentSourceChange={handleDocumentSourceChange}
              onVideoSourceChange={handleVideoSourceChange}
              showOrganization={false}
              groupsDisabled
              instructors={instructors}
            />
          </Modal.Body>
          <Modal.Footer className="bg-gray-100 border-0 rounded-b-2xl p-4">
            <Row className="w-100 g-3">
              <Col md={6}>
                <Button bsPrefix="custom-hamburger" onClick={() => setShowModal(false)} disabled={modalLoading} className="w-full border-1 bg-gray-100 hover:bg-gray-200 text-gray-700 border-2 border-gray-300 hover:border-gray-500 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2">
                  Cancel
                </Button>
              </Col>
              <Col md={6}>
                <Button
                  bsPrefix="custom-hamburger"
                  type="submit"
                  disabled={modalLoading}
                  className="w-full border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2"
                >
                  {modalLoading ? (
                    <>
                      <Spinner size="sm" className="me-2" />
                      Saving...
                    </>
                  ) : (
                    editingVlt ? 'Update VLT' : 'Create VLT'
                  )}
                </Button>
              </Col>
            </Row>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={handleDeleteCancel} centered>
        <Modal.Header closeButton closeVariant="white" className="bg-gradient-to-r from-purple-600 to-pink-600 text-white border-0 py-3 px-4 sm:px-4 sm:py-4 modal-header">
          <Modal.Title className="font-bold text-xl text-white">Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body className="bg-gray-50 p-3 sm:p-5 text-center py-10">
          <div className="mb-4">
            <FaTrash size={60} className="text-red-500 mx-auto" />
          </div>
          <h5 className="font-bold text-gray-800 mb-3">Delete VLT</h5>
          <p className="text-gray-600">
            Are you sure you want to delete <strong>"{vltToDelete?.title}"</strong>? This action cannot be undone.
          </p>
        </Modal.Body>
        <Modal.Footer className="bg-gray-100 border-0 rounded-b-2xl p-4">
          <Row className="w-100 g-3">
            <Col md={6}>
              <Button bsPrefix="custom-hamburger" onClick={handleDeleteCancel} className="w-full border-1 bg-gray-100 hover:bg-gray-200 text-gray-700 border-2 border-gray-300 hover:border-gray-500 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2">
                Cancel
              </Button>
            </Col>
            <Col md={6}>
              <Button
                bsPrefix="custom-hamburger"
                onClick={confirmDelete}
                disabled={deleteLoading}
                className="w-full border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2"
              >
                {deleteLoading ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Deleting...
                  </>
                ) : (
                  'Delete VLT'
                )}
              </Button>
            </Col>
          </Row>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default OrgVltList;